/**
 * ★ THE PLATFORM'S OWN NOW, READ ONCE ON EVERY CALENDAR VISIT - the month its clock is in, the
 * Calendar's third source for its opening month (./CalendarRoute.tsx, the header's "THE MONTH"),
 * added after the owner's live walk of real cabinets on 2026-10-01; and the DAY its clock is in,
 * which the month grid marks as today (the owner's list, 2026-10-05, item 7).
 *
 * WHAT WAS WRONG. With no notable date ahead - every cabinet on a database where editorial has not
 * added events yet, which is launch - `getPublishDateSuggestions` answers an empty list, and the
 * screen showed "Which month?" over a chooser set to January 2026. The owner read it as the
 * calendar opening on an empty January. And the month grid had no today at all.
 *
 * WHY NOT THE BROWSER. CalendarRoute's header and `features/cabinet/format.ts` say why this app keeps
 * no clock (C11, D9), and nothing here changes that. The month and the day have to come from a
 * RESPONSE.
 *
 * WHY `getPaymentsView`. It is the one read on authors-api that reports the server's own now:
 * `computedAt` is "when these sums were taken", the injected clock's instant (`computedAt:
 * now.toISOString()` in src/modules/money/queries/payments-view.service.ts), so `CLOCK_MODE=test`
 * moves it like everything else. `getDashboard`'s `to` is the last COMPLETED day - yesterday - and
 * would open the previous month on the first of every month, and mark yesterday as today. The read
 * changes nothing, and the Overview makes it on every visit.
 *
 * ★ ONE READ, TWO USES (2026-10-05). It used to be made only when it was wanted to decide the
 * month: no month in the address and the suggestions answered (or failed) without one - or the
 * reader pressed "This month". The grid's today wants it on every visit, so it is now made ONCE,
 * when the screen mounts, whatever else is known. WHAT IT DECIDES DID NOT CHANGE: `month` is handed
 * back only while it is wanted, so the order stays the address, then the suggestion, then this. An
 * answer that lands before the suggestions do cannot open the screen on its month, and beside a
 * month from the address it is used for nothing but the day. `today` is handed back as soon as the
 * read has answered.
 *
 * THE DAY IS CUT AS THE GRID CUTS ITS DAYS. `dayOfInstant` (features/calendar/domain/month.ts)
 * takes the UTC day of `computedAt` - the calendar `daysOfMonth` spells every cell's key in, and
 * the one `monthOfInstant` cuts the month in - so the mark lands on the cell the grid draws for
 * that instant, and the month and the day cannot disagree. It is the day of the READ: a screen left
 * open across midnight marks the day it was read on until the next visit.
 *
 * A FAILED READ IS NOT A GUESS. It leaves `month` and `today` undefined and `pending` false: no day
 * is marked, and the screen asks for a month only where it did before - when neither the address
 * nor the suggestions gave one.
 *
 * ★ AND THE INSTANT ITSELF (design 24, T29). `instant` is `computedAt` as read, for a screen whose
 * day is not the grid's: Setup's "Publication date and time" is on the author's clock, so its first
 * pickable day is this instant's day IN THE AUTHOR'S ZONE (editor/SetupPanel.tsx) - the UTC `today`
 * would refuse an author's own evening west of UTC and offer their yesterday east of it.
 *
 * NO LIVENESS REF. Each run of the effect owns its `AbortController`, and an aborted read is
 * ignored, so StrictMode's mount-unmount-mount cancels the first read and the second one lands -
 * the shape the cabinet's `useAsync` has, and the one that does not lose an answer in development.
 * Leaving the screen aborts the read too, and that is not a failure either.
 *
 * ★ AND WHEN THE READER ASKS FOR "THIS MONTH" (design 15, the owner's live look at real cabinets,
 * 2026-10-01: the prototype's "This week" control beside the ‹ › row): `asked` makes the month
 * wanted, and the answer already in hand is used - never a second read, a failed answer included
 * (the screen then says the current month could not be read, and the month chooser is still
 * there). Pressed before the visit's read has landed, it waits for that same read.
 */
import { useEffect, useState } from 'react';
import { useCabinetApi } from '../../features/cabinet/api';
import { dayOfInstant, monthOfInstant } from '../../features/calendar/domain/month';
import type { MonthKey } from '../../features/calendar/domain/month';

export interface PlatformMonth {
  /**
   * The UTC month the platform's clock is in, while the screen wants it to decide the month - none
   * known and the suggestions answered, or `asked`. Undefined otherwise, before the read, or when
   * it failed.
   */
  readonly month: MonthKey | undefined;
  /** True while that month is wanted and the read has not answered: the screen waits, not asks. */
  readonly pending: boolean;
  /** The UTC day the platform's clock is in, `YYYY-MM-DD` as a grid cell's key, once read;
   *  undefined before, or when it failed. */
  readonly today: string | undefined;
  /** The platform's own instant, `computedAt` as read (the header's "THE INSTANT ITSELF");
   *  undefined before, or when it failed. */
  readonly instant: string | undefined;
}

/** What the visit's one read answered: all undefined when it failed. */
interface PlatformNow {
  readonly month: MonthKey | undefined;
  readonly day: string | undefined;
  readonly instant: string | undefined;
}

/**
 * `known` is the month the screen already has - from the address or from the suggestions - and
 * `suggestions` the status of that read. The platform's month is handed back only when there is no
 * month and the suggestions have answered - or when `asked`: the reader pressed "This month". The
 * read itself is made once per visit either way (the header's "ONE READ, TWO USES").
 */
export function usePlatformMonth(
  known: MonthKey | undefined,
  suggestions: 'loading' | 'ready' | 'error',
  asked = false,
): PlatformMonth {
  const cabinet = useCabinetApi();
  const wanted = asked || (known === undefined && suggestions !== 'loading');
  const [answer, setAnswer] = useState<PlatformNow | undefined>(undefined);

  useEffect(() => {
    const controller = new AbortController();
    cabinet.getPaymentsView(controller.signal).then(
      (view) => {
        setAnswer({
          month: monthOfInstant(view.computedAt),
          day: dayOfInstant(view.computedAt),
          instant: view.computedAt,
        });
      },
      (cause: unknown) => {
        // Leaving the screen aborts the read; that is not a failure to report.
        if (!(cause instanceof DOMException && cause.name === 'AbortError')) {
          setAnswer({ month: undefined, day: undefined, instant: undefined });
        }
      },
    );
    return () => {
      controller.abort();
    };
  }, [cabinet]);

  return {
    month: wanted ? answer?.month : undefined,
    pending: wanted && answer === undefined,
    today: answer?.day,
    instant: answer?.instant,
  };
}
